import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Grid, Card, CardContent,
  CircularProgress, Alert, Tabs, Tab, Divider, Paper, MenuItem
} from '@mui/material';
import { projectService } from '../../services/projectService';
import { dashboardService } from '../../services/dashboardService';
import VisitorsChart from '../../components/Analytics/VisitorsChart';
import MetricComparison from '../../components/Analytics/MetricComparison';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const isNew = id === 'new' || !id;
  const [tabIndex, setTabIndex] = useState(isNew ? 1 : 0);

  const [project, setProject] = useState({
    name: '',
    allowedDomains: '',
    apiKey: '',
    status: 'active',
    enabledFeatures: ['traffic']
  });

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [range, setRange] = useState('7days');
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  useEffect(() => {
    if (!isNew) {
      fetchProject();
      fetchDashboard();
    }
  }, [id, isNew, range]);

  const fetchDashboard = async () => {
    try {
      setDashboardLoading(true);
      const data = await dashboardService.getContactDashboardData(id, range);
      setDashboardData(data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setDashboardLoading(false);
    }
  };

  const fetchProject = async () => {
    try {
      setLoading(true);
      const data = await projectService.getProject(id);
      setProject(data);
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Failed to load project details.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProject(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (isNew) {
        const created = await projectService.createProject(project);
        setSuccess('Project created successfully!');
        setTimeout(() => navigate(`/project/${created._id}`), 1000);
      } else {
        await projectService.updateProject(id, project);
        setSuccess('Project updated successfully!');
      }
    } catch (err) {
      console.error('Error saving project:', err);
      setError(err.response?.data?.message || 'Failed to save project.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h4" component="h1">
          {isNew ? 'Create New Project' : project.name}
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/project')}>
          Back to Projects
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Overview" disabled={isNew} />
          <Tab label="Settings" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Overview Tab Content */}
          {tabIndex === 0 && !isNew && (
            <Box>
              <Card sx={{ mb: 4, bgcolor: 'background.default' }}>
                <CardContent>
                  <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Typography variant="h5" gutterBottom>{project.name}</Typography>
                      <Typography color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box component="span" sx={{
                          width: 10, height: 10, borderRadius: '50%',
                          bgcolor: project.status === 'active' ? 'success.main' : 'error.main'
                        }} />
                        {project.status || 'Unknown'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <Grid container spacing={2} textAlign="center">
                        <Grid item xs={4}>
                          <Typography variant="h4" color="primary.main">{project.activitycount || 0}</Typography>
                          <Typography variant="subtitle2" color="text.secondary">Activities</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ borderLeft: '1px solid #ddd', borderRight: '1px solid #ddd' }}>
                          <Typography variant="h4" color="primary.main">{project.contactcount || 0}</Typography>
                          <Typography variant="subtitle2" color="text.secondary">Contacts</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="h4" color="primary.main">{project.anonymouscount || 0}</Typography>
                          <Typography variant="subtitle2" color="text.secondary">Visitors</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate(`/project/${project._id}/contactdashboard`)}
                  >
                    View Dashboard
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate(`/project/${project._id}/contact`)}
                  >
                    See all Contacts
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate(`/project/${project._id}/anonymous`)}
                  >
                    See all Visitors
                  </Button>
                </Grid>
              </Grid>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Performance Overview</Typography>
                <TextField
                  select
                  size="small"
                  value={range}
                  onChange={(e) => setRange(e.target.value)}
                  sx={{ width: 160 }}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="7days">Last 7 Days</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                </TextField>
              </Box>

              <MetricComparison data={dashboardData} loading={dashboardLoading} />

              {/* New Analytics Visualization */}
              <VisitorsChart data={dashboardData?.trafficTrend} />

              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Configuration</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Add this snippet to your website
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      p: 2,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      overflowX: 'auto',
                      fontSize: '0.875rem'
                    }}
                  >
                    {`<script src="${window.location.origin}/public/lib/analytics.js"></script>
<script>
  iga.load("${project.apiKey || 'YOUR_API_KEY'}");
  iga.page();
</script>`}
                  </Box>

                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Identify User (Optional)</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Use this to identify your users for better tracking
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      p: 2,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      overflowX: 'auto',
                      fontSize: '0.875rem'
                    }}
                  >
                    {`<script>
  iga.identify('<Unique User Id>', {
    email: 'scott@example.com',
    first_name: 'Scott',
    last_name: 'Tiger'
  });
</script>`}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Settings Tab Content */}
          {tabIndex === 1 && (
            <Box component="form" noValidate sx={{ mt: 1, maxWidth: 600 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Name"
                    name="name"
                    value={project.name || ''}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Allowed Domains"
                    name="allowedDomains"
                    placeholder="e.g. facebook.com, google.com"
                    value={project.allowedDomains || ''}
                    onChange={handleChange}
                    helperText="Comma separated list of allowed domains"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="API Key"
                    name="apiKey"
                    value={project.apiKey || ''}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    name="status"
                    value={project.status || 'active'}
                    onChange={handleChange}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    SelectProps={{ multiple: true }}
                    label="Enabled Use Cases / Features"
                    name="enabledFeatures"
                    value={project.enabledFeatures || ['traffic']}
                    onChange={(e) => {
                      const value = e.target.value;
                      setProject(prev => ({
                        ...prev,
                        enabledFeatures: typeof value === 'string' ? value.split(',') : value
                      }));
                    }}
                    helperText="Select all the use cases that apply to this project."
                  >
                    <MenuItem value="traffic">Web Analytics (Track basic traffic)</MenuItem>
                    <MenuItem value="crm">CRM (Track users and events)</MenuItem>
                    <MenuItem value="automations">Automations (Trigger emails based on behavior)</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Project'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ProjectDetail;
